import { useCallback, useEffect, useRef } from 'react'
import type { JSX } from 'react'
import { useParams, useNavigate, useResolvedPath } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { readFragment } from '~publish/graphql'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import { PostCard_Post } from '../PostCard'
import { useOnPostComposerClose } from './helpers'
import { GetPost } from './PostDetailsDialog'
import { PostErrorDialog } from './PostErrorDialog'

export function PostEditDialog(): JSX.Element | null {
  const resolvedPath = useResolvedPath('.')
  const { postId, id: channelId } = useParams<{ postId: string; id: string }>()
  const hasOpenedComposerRef = useRef(false)
  const { editPostInComposer } = usePostComposer()
  const navigate = useNavigate()
  const { data, loading, error } = useQuery(GetPost, {
    variables: {
      id: postId ?? '',
    },
  })
  const post = data?.post ? readFragment(PostCard_Post, data.post) : undefined

  useEffect(() => {
    if (post && !hasOpenedComposerRef.current) {
      hasOpenedComposerRef.current = true
      void editPostInComposer({
        postId: postId ?? '',
        contentItemId: post.postGroup?.id,
        contentItemTitle: post.postGroup?.title || null,
      })
    }
    // We only want to open the composer once on mount, not when editPostInComposer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, channelId, post])

  const handleComposerClose = useCallback(() => {
    navigate('..')
  }, [navigate])

  useOnPostComposerClose(handleComposerClose)

  if (error) {
    return <PostErrorDialog error={error} />
  }

  if (data && !loading) {
    // This logic corrects the URL if the loaded post is from a different channel
    // This is an edge-case since we shouldn't link to a different channel on purpose.
    if (
      channelId &&
      post?.channel?.id &&
      channelId !== post.channel.id &&
      resolvedPath?.pathname
    ) {
      navigate(resolvedPath.pathname.replace(channelId, post.channel.id), {
        replace: true,
      })
    }
  }

  return null
}
